import { FC, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import UploadDropzone from 'components/uploads/components/UploadDropzone'
import { FileDown, File, Paperclip, X } from 'lucide-react'
import { Button, type UploadFileProps } from '@wellesley/ui'
import { toast } from '@wellesley/system-toast'
import { platformSettingsSlice } from '@wellesley/settings/slices/platformSettingsSlice'
import checkUploadLimits from '@wellesley/uploads/helpers/checkUploadLimits'

import { type FileUploadProps } from './types'

const FileUpload: FC<FileUploadProps> = ({ files, onFilesChange }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const filesRef = useRef(files)
  filesRef.current = files

  useEffect(() => {
    return () => {
      filesRef.current.forEach((f) => URL.revokeObjectURL(f.objectUrl))
    }
  }, [])

  const imgPolicy = useSelector(
    platformSettingsSlice.selectors.selectImagePolicy,
  )
  const videoPolicy = useSelector(
    platformSettingsSlice.selectors.selectVideoPolicy,
  )
  const docPolicy = useSelector(platformSettingsSlice.selectors.selectDocPolicy)

  const handleNewFiles = async (newFiles: UploadFileProps[]) => {
    const valid: UploadFileProps[] = []

    for (const media of newFiles) {
      const error = await checkUploadLimits({
        media,
        imgPolicy,
        docPolicy,
        videoPolicy,
        audioPolicy: { extensions: [], maxSize: 0, keepOriginal: false },
      })

      if (error) {
        URL.revokeObjectURL(media.objectUrl)
        toast.error(error)
      } else {
        valid.push(media)
      }
    }

    if (valid.length > 0) {
      onFilesChange([...files, ...valid])
    }
  }

  const handleRemove = (index: number) => {
    URL.revokeObjectURL(files[index].objectUrl)
    const updated = files.filter((_, i) => i !== index)
    onFilesChange(updated)
  }

  return (
    <div className="gap-s flex flex-col">
      <UploadDropzone
        addMedia={(f) => void handleNewFiles(f)}
        className="rounded-xl"
      >
        <div className="rounded-m p-m flex items-center border border-dashed border-gray-300">
          <FileDown
            strokeWidth={1.5}
            size={48}
            className="mr-xs shrink-0 text-gray-700"
          />

          <div className="gap-xxxs flex flex-1 flex-col">
            <p className="text-base font-semibold text-gray-900">
              Upload or drag a files here (optional)
            </p>
            <p className="text-base text-gray-500">
              Add files that will help to detect the issue
            </p>
          </div>

          <Button
            size="m"
            variant="secondary"
            onClick={() => inputRef.current?.click()}
          >
            Select files
          </Button>

          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,video/*,application/pdf"
            className="hidden"
            onChange={(e) => {
              if (!e.target.files) return
              const mapped = Array.from(e.target.files).map((file) => ({
                file,
                objectUrl: URL.createObjectURL(file),
              }))
              void handleNewFiles(mapped)
              e.target.value = ''
            }}
          />
        </div>
      </UploadDropzone>

      {files.length === 0 ? (
        <div className="gap-xxs flex items-center text-sm text-gray-400">
          <Paperclip size={14} />
          <span>No file added</span>
        </div>
      ) : (
        <div className="gap-xxs flex flex-wrap">
          {files.map((f, i) => (
            <div
              key={f.objectUrl}
              className="gap-xxxs px-xxs h-s flex items-center rounded-sm bg-gray-100 text-sm text-gray-900"
            >
              <File size={16} className="shrink-0 text-gray-900" />
              <span className="max-w-[160px] truncate">{f.file.name}</span>
              <button
                type="button"
                onClick={() => handleRemove(i)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default FileUpload
